import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Archive, RotateCcw } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("da-DK", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

// F1: knap + dialog med arkiverede poster af én type og "Gendan". Kun tilføjelse – ændrer ikke eksisterende lister.
export function ArchiveButton({ kind, label = "Arkiverede", onRestored, className = "" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const load = () => api.get(`/archive/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = async (it) => {
    try { await api.post(`/archive/${kind}/${it.id}/restore`); toast.success("Gendannet"); load(); onRestored?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className={`rounded-xl text-stone-500 hover:text-stone-900 ${className}`} data-testid={`archive-btn-${kind}`}>
        <Archive className="h-4 w-4 mr-1.5" strokeWidth={1.75} /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl sm:max-w-[640px]" data-testid={`archive-dialog-${kind}`}>
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Arkiverede</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">Arkiverede poster tæller ikke med i oversigter. De kan gendannes her.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-[#EBE8E3] -mx-1">
            {items === null && <p className="text-sm text-stone-400 py-6 px-1">Indlæser…</p>}
            {items && items.length === 0 && <p className="text-sm text-stone-400 py-6 px-1" data-testid="archive-empty">Ingen arkiverede poster.</p>}
            {items?.map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-3 py-2.5 px-1" data-testid={`archive-row-${it.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A1919] truncate">{it.no ? <span className="font-mono text-xs text-stone-500 mr-2">{it.no}</span> : null}{it.label || "Uden titel"}</p>
                  <p className="text-xs text-stone-400">Arkiveret {fmt(it.archived?.at)} af {it.archived?.by_name || "—"}{it.status ? ` · ${it.status}` : ""}</p>
                </div>
                <Button size="sm" variant="outline" className="rounded-xl border-[#EBE8E3] shrink-0" onClick={() => restore(it)} data-testid={`archive-restore-${it.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Gendan</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
